// frontend/src/pages/SubmitDamageReport.jsx
//
// Module A — formulaire de signalement de dégâts par un agriculteur.
// POST /api/v1/depredation/reports avec photos (multipart) + géoloc.
// La géoloc est récupérée via navigator.geolocation (GPS du device)
// avec fallback à la saisie manuelle lat/lng.

import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LangContext } from '../App';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

const T = {
  fr: {
    title: 'Signaler un dégât',
    subtitle: 'Renseignez l’espèce, le type de dégât et localisez précisément.',
    species: 'Espèce concernée',
    speciesPlaceholder: 'Sélectionnez…',
    damageType: 'Type de dégât',
    damageTypes: {
      cultures: 'Cultures',
      betail: 'Bétail',
      equipement: 'Équipement',
      structures: 'Structures',
      paturages: 'Pâturages',
    },
    description: 'Description',
    descriptionPlaceholder: 'Étendue, dégâts visibles, date approximative…',
    location: 'Localisation',
    useGps: 'Utiliser ma position GPS',
    gettingGps: 'Récupération GPS…',
    gpsFailed: 'GPS indisponible — saisissez les coordonnées manuellement.',
    lat: 'Latitude',
    lng: 'Longitude',
    photos: 'Photos (optionnel, jusqu’à 10)',
    submit: 'Soumettre le signalement',
    submitting: 'Envoi…',
    successTitle: 'Signalement enregistré',
    successMessage: 'Les chasseurs proches verront votre demande.',
    viewMyReports: 'Voir mes signalements',
    submitAnother: 'Soumettre un autre signalement',
    errGeneric: 'Erreur — veuillez réessayer.',
  },
  en: {
    title: 'Report damage',
    subtitle: 'Tell us the species, damage type, and where it happened.',
    species: 'Species',
    speciesPlaceholder: 'Select…',
    damageType: 'Damage type',
    damageTypes: {
      cultures: 'Crops',
      betail: 'Livestock',
      equipement: 'Equipment',
      structures: 'Structures',
      paturages: 'Pastures',
    },
    description: 'Description',
    descriptionPlaceholder: 'Extent, visible damage, approximate date…',
    location: 'Location',
    useGps: 'Use my GPS position',
    gettingGps: 'Fetching GPS…',
    gpsFailed: 'GPS unavailable — enter coordinates manually.',
    lat: 'Latitude',
    lng: 'Longitude',
    photos: 'Photos (optional, up to 10)',
    submit: 'Submit report',
    submitting: 'Sending…',
    successTitle: 'Report saved',
    successMessage: 'Hunters nearby will see your request.',
    viewMyReports: 'View my reports',
    submitAnother: 'Submit another report',
    errGeneric: 'Error — please try again.',
  },
};

export default function SubmitDamageReport() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;
  const navigate = useNavigate();

  const [species, setSpecies] = useState('');
  const [damageType, setDamageType] = useState('');
  const [description, setDescription] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gettingGps, setGettingGps] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Référentiels — chargés une fois au mount. Le backend les sert public ;
  // on n'a pas besoin d'être authentifié pour fetcher mais on garde le
  // header car api injecte le token de toute façon.
  const [especes, setEspeces] = useState([]);
  useEffect(() => {
    api
      .get('/depredation/especes')
      .then((r) => setEspeces(r.data))
      .catch(() => {});
  }, []);

  const getGps = () => {
    if (!navigator.geolocation) {
      setError(t.gpsFailed);
      return;
    }
    setGettingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setGettingGps(false);
        setError('');
      },
      () => {
        setGettingGps(false);
        setError(t.gpsFailed);
      },
      { enableHighAccuracy: true, timeout: 10_000 }
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // FormData parce qu'on a des photos (multipart). Si pas de photo,
      // on aurait pu faire un POST JSON mais FormData accepte les deux.
      const fd = new FormData();
      fd.append('species', species);
      fd.append('damage_type', damageType);
      fd.append('description', description);
      fd.append('lat', lat);
      fd.append('lng', lng);
      for (const file of photos) fd.append('photos', file);
      await api.post('/depredation/reports', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || t.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <PrivateLayout>
        <main style={{ maxWidth: 600, margin: '0 auto', padding: '2rem' }}>
          <PageHeader icon="✅" title={t.successTitle} subtitle={t.successMessage} />
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button onClick={() => navigate('/my-damage-reports')} style={btnPrimary} type="button">
              {t.viewMyReports}
            </button>
            <button
              onClick={() => {
                setDone(false);
                setSpecies('');
                setDamageType('');
                setDescription('');
                setLat('');
                setLng('');
                setPhotos([]);
              }}
              style={btnSecondary}
              type="button"
            >
              {t.submitAnother}
            </button>
          </div>
        </main>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <main style={{ maxWidth: 600, margin: '0 auto', padding: '2rem' }}>
        <PageHeader icon="🌾" title={t.title} subtitle={t.subtitle} />

        <form onSubmit={onSubmit} style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
          <label style={labelStyle}>
            <span style={fieldLabel}>{t.species}</span>
            <select
              required
              value={species}
              onChange={(e) => setSpecies(e.target.value)}
              style={inputStyle}
            >
              <option value="">{t.speciesPlaceholder}</option>
              {especes.map((e) => (
                <option key={e.code} value={e.code}>
                  {lang === 'en' ? e.en : e.fr}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            <span style={fieldLabel}>{t.damageType}</span>
            <select
              required
              value={damageType}
              onChange={(e) => setDamageType(e.target.value)}
              style={inputStyle}
            >
              <option value="">{t.speciesPlaceholder}</option>
              {Object.entries(t.damageTypes).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            <span style={fieldLabel}>{t.description}</span>
            <textarea
              required
              minLength={10}
              maxLength={2000}
              rows={4}
              placeholder={t.descriptionPlaceholder}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </label>

          <div>
            <div style={fieldLabel}>{t.location}</div>
            <button type="button" onClick={getGps} disabled={gettingGps} style={btnSecondary}>
              📍 {gettingGps ? t.gettingGps : t.useGps}
            </button>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem',
                marginTop: '0.5rem',
              }}
            >
              <label>
                <span style={{ ...fieldLabel, fontSize: '0.75rem' }}>{t.lat}</span>
                <input
                  type="number"
                  step="any"
                  required
                  min="-90"
                  max="90"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  style={inputStyle}
                />
              </label>
              <label>
                <span style={{ ...fieldLabel, fontSize: '0.75rem' }}>{t.lng}</span>
                <input
                  type="number"
                  step="any"
                  required
                  min="-180"
                  max="180"
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  style={inputStyle}
                />
              </label>
            </div>
          </div>

          <label style={labelStyle}>
            <span style={fieldLabel}>{t.photos}</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setPhotos(Array.from(e.target.files).slice(0, 10))}
              style={{ ...inputStyle, padding: '0.5rem' }}
            />
            {photos.length > 0 && (
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)' }}>
                {photos.length} fichier(s)
              </div>
            )}
          </label>

          {error && <div style={{ color: '#ff6b6b', fontSize: '0.86rem' }}>{error}</div>}

          <button type="submit" disabled={loading} style={btnPrimary}>
            {loading ? t.submitting : t.submit}
          </button>
        </form>

        <p style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.86rem' }}>
          <Link to="/my-damage-reports" style={{ color: 'var(--couleur-or)' }}>
            {t.viewMyReports} →
          </Link>
        </p>
      </main>
    </PrivateLayout>
  );
}

// ── styles partagés (inline pour rester cohérent avec le reste de l'app) ────

const fieldLabel = {
  fontSize: '0.86rem',
  marginBottom: '0.35rem',
  display: 'block',
  color: 'rgba(255,255,255,0.7)',
};

const labelStyle = { display: 'block' };

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: '6px',
  color: 'var(--couleur-texte)',
  padding: '0.7rem 0.9rem',
  fontSize: '0.92rem',
  boxSizing: 'border-box',
};

const btnPrimary = {
  width: '100%',
  padding: '0.85rem',
  background: 'var(--couleur-or)',
  color: '#000',
  border: 'none',
  borderRadius: '6px',
  fontWeight: 600,
  cursor: 'pointer',
};

const btnSecondary = {
  padding: '0.65rem 1rem',
  background: 'transparent',
  color: 'var(--couleur-or)',
  border: '1px solid var(--couleur-or)',
  borderRadius: '6px',
  fontWeight: 500,
  cursor: 'pointer',
};
